import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'kingpin/atoms/Button';

import STORE from 'store';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import useCreateDashboardCopy from 'hooks/useCreateDashboardCopy';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardsContext from 'contexts/DashboardsContext';
import AllDefinitionsGate from 'components/AllDefinitionsGate';
import Typography from 'kingpin/atoms/Typography';
import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';

import useEntityParams from '../../SelectedEntityProvider/useEntityParams';
import useDashboardOptions from './useDashboardOptions';
import usePerformanceBoardOptions from './usePerformanceBoardOptions';
import useAddTabTypeOptions from './useAddTabTypeOptions';
import Form from '../../../../kingpin/forms/Form';
import FormHeader from '../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../kingpin/forms/FormContent';
import useReportOptions from './useReportOptions';
import useMarkReportAsFilteredByEntity from './useMarkReportAsFilteredByEntity';
import useMarkBoardAsFilteredByEntity from './useMarkBoardAsFilteredByEntity';
import useBonusProgramOptions from './useBonusProgramOptions';
import useCustomerLaneCommitmentBoardOptions from './useCustomerLaneCommitmentBoardOptions';
import useCreateScorecardCopy from './useCreateScorecardCopy';
import useScorecardOptions from './useScorecardOptions';
import LabelsPicker from '../../../../components/Inputs/LabelsPicker';

const DASHBOARD_TAB_ACCESS: ResourceAccess = {
  type: 'Private',
  version: '2',
};

const DASHBOARD_TAB_V3_ACCESS: V3Access = {
  isPublic: false,
  groupIds: [],
  userIds: [],
};

const AddTabWizard = ({
  close,
  app,
  formTitle,
  buildTabLink,
}: {
  close: () => void;
  app: EntityDetails.App;
  formTitle: string;
  buildTabLink: ({
    entityApp,
    value,
    tabType,
    tabId,
  }: {
    entityApp: EntityDetails.App;
    value: string;
    tabType: EntityDetails.TabType;
    tabId: string;
  }) => string;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const navigate = useNavigate();
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { value } = useEntityParams();
  const [title, setTitle] = useState<string>('');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<
    DashboardType | undefined
  >();
  const [selectedPerformanceBoard, setSelectedPerformanceBoard] = useState<
    PerformanceBoardTypes.Board | undefined
  >();
  const [
    selectedCustomerLaneCommitmentsBoard,
    setSelectedCustomerLaneCommitmentsBoard,
  ] = useState<CustomerLaneBoard | undefined>();

  const [selectedReport, setSelectedReport] = useState<
    ReportType | undefined
  >();
  const [selectedBonusProgram, setSelectedBonusProgram] = useState<
    BonusTypes.BonusProgram | undefined
  >();
  const [selectedScorecard, setSelectedScorecard] = useState<
    Scorecards.Scorecard | undefined
  >();
  const { addTabType, options } = useAddTabTypeOptions({
    setTitle,
    setSelectedDashboard,
    setSelectedPerformanceBoard,
    setSelectedCustomerLaneCommitmentsBoard,
    setSelectedReport,
    setSelectedBonusProgram,
    setSelectedScorecard,
    setLabelIds,
  });

  const onTitleChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  }, []);

  const dashboardOptions = useDashboardOptions({
    selectedDashboard,
    setSelectedDashboard,
    setTitle,
  });
  const performanceBoardOptions = usePerformanceBoardOptions({
    selectedPerformanceBoard,
    setSelectedPerformanceBoard,
    setTitle,
  });
  const customerLaneCommitmentBoardOptions =
    useCustomerLaneCommitmentBoardOptions({
      selectedCustomerLaneCommitmentsBoard,
      setSelectedCustomerLaneCommitmentsBoard,
      setTitle,
    });
  const reportOptions = useReportOptions({
    selectedReport,
    setSelectedReport,
    setTitle,
  });
  const bonusProgramOptions = useBonusProgramOptions({
    selectedBonusProgram,
    setSelectedBonusProgram,
    setTitle,
  });
  const scorecardOptions = useScorecardOptions({
    selectedScorecard,
    setSelectedScorecard,
    setTitle,
  });

  const createDashboardCopy = useCreateDashboardCopy();
  const createScorecardCopy = useCreateScorecardCopy();
  const markReportAsFilteredByEntity = useMarkReportAsFilteredByEntity();
  const markBoardAsFilteredByEntity = useMarkBoardAsFilteredByEntity();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const addTabToApp = useCallback(
    async ({
      type,
      typeId,
    }: {
      type:
        | 'dashboard'
        | 'board'
        | 'report'
        | 'driver-bonus-program-report'
        | 'driver-bonus-combined'
        | 'driver-details-tab'
        | 'scorecard';
      typeId: string;
    }) => {
      const newTab: EntityDetails.PersistedTab = {
        type,
        typeId,
      };
      const newApp: EntityDetails.App = {
        ...app,
        tabs: [...app.tabs, newTab],
      };

      STORE.contentDistributions
        .getEntityDetailsApps({
          accountId: selectedAccountId,
        })
        .doc(app.id)
        .set(newApp)
        .then(() => {
          setIsLoading(false);
          close();
          navigate(
            buildTabLink({
              entityApp: app,
              value,
              tabType: newTab.type,
              tabId: newTab.typeId,
            }),
          );
        });
    },
    [app, buildTabLink, close, navigate, selectedAccountId, value],
  );
  const onNewDashboardSave = useCallback(async () => {
    if (addTabType !== 'new-dashboard' || isLoading) {
      return;
    }
    setIsLoading(true);

    const newDashboard: DashboardType = {
      id: getIdentifier(),
      isTemplate: false,
      variableDrillDowns: [],
      createdBy: currentUser.id,
      createdOn: getTimeStamp(),
      updatedBy: currentUser.id,
      updatedOn: getTimeStamp(),
      name: title,
      canvas: { cards: [] },
      access: DASHBOARD_TAB_ACCESS,
      v3Access: DASHBOARD_TAB_V3_ACCESS,
      isV3Published: false,
      version: '5',
      isEntityDetailsTab: true,
      labelIds,
    };

    await STORE.getDashboardsRef({ accountId: selectedAccountId })
      .doc(newDashboard.id)
      .set(newDashboard);
    addOrUpdateDashboard(newDashboard);
    await addTabToApp({
      type: 'dashboard',
      typeId: newDashboard.id,
    });
    setIsLoading(false);
  }, [
    addOrUpdateDashboard,
    addTabToApp,
    addTabType,
    currentUser.id,
    isLoading,
    labelIds,
    selectedAccountId,
    title,
  ]);

  const onCopyDashboardSave = useCallback(async () => {
    if (addTabType !== 'copy-dashboard' || isLoading || !selectedDashboard) {
      return;
    }
    setIsLoading(true);

    const { newDashboardId } = await createDashboardCopy({
      name: title,
      dashboard: {
        ...selectedDashboard,
        isEntityDetailsTab: true,
        labelIds,
      },
      access: DASHBOARD_TAB_ACCESS,
    });
    await addTabToApp({
      type: 'dashboard',
      typeId: newDashboardId,
    });
    setIsLoading(false);
  }, [
    addTabToApp,
    addTabType,
    createDashboardCopy,
    isLoading,
    labelIds,
    selectedDashboard,
    title,
  ]);

  const onAddDashboardSave = useCallback(async () => {
    if (addTabType !== 'add-dashboard' || isLoading || !selectedDashboard) {
      return;
    }
    setIsLoading(true);
    await addTabToApp({
      type: 'dashboard',
      typeId: selectedDashboard.id,
    });
    setIsLoading(false);
  }, [addTabToApp, addTabType, isLoading, selectedDashboard]);

  const onAddPerformanceBoardSave = useCallback(async () => {
    if (
      addTabType !== 'add-performance-board' ||
      isLoading ||
      !selectedPerformanceBoard
    ) {
      return;
    }
    setIsLoading(true);
    await markBoardAsFilteredByEntity(selectedPerformanceBoard);

    await addTabToApp({
      type: 'board',
      typeId: selectedPerformanceBoard.id,
    });
    setIsLoading(false);
  }, [
    addTabToApp,
    addTabType,
    isLoading,
    markBoardAsFilteredByEntity,
    selectedPerformanceBoard,
  ]);

  const onAddReportSave = useCallback(async () => {
    if (addTabType !== 'add-report' || isLoading || !selectedReport) {
      return;
    }
    setIsLoading(true);
    await markReportAsFilteredByEntity(selectedReport);
    await addTabToApp({
      type: 'report',
      typeId: selectedReport.id,
    });
    setIsLoading(false);
  }, [
    addTabToApp,
    addTabType,
    isLoading,
    markReportAsFilteredByEntity,
    selectedReport,
  ]);

  const onAddDriverBonusProgramReportSaved = useCallback(async () => {
    if (
      addTabType !== 'add-driver-bonus-program-report' ||
      isLoading ||
      !selectedBonusProgram
    ) {
      return;
    }
    setIsLoading(true);
    await addTabToApp({
      type: 'driver-bonus-program-report',
      typeId: selectedBonusProgram.id,
    });
    setIsLoading(false);
  }, [addTabToApp, addTabType, isLoading, selectedBonusProgram]);

  const onAddDriverDetailsSaved = useCallback(async () => {
    if (addTabType !== 'add-driver-details' || isLoading) {
      return;
    }
    setIsLoading(true);
    await addTabToApp({
      type: 'driver-details-tab',
      typeId: 'x',
    });
    setIsLoading(false);
  }, [addTabToApp, addTabType, isLoading]);

  const onAddDriverBonusCombinedSaved = useCallback(async () => {
    if (addTabType !== 'add-driver-bonus-combined' || isLoading) {
      return;
    }
    setIsLoading(true);
    // Renders all bonus programs, so there is no specific artifact to reference.
    await addTabToApp({
      type: 'driver-bonus-combined',
      typeId: 'x',
    });
    setIsLoading(false);
  }, [addTabToApp, addTabType, isLoading]);

  const onAddCustomerLaneCommitmentsBoardSave = useCallback(async () => {
    if (
      addTabType !== 'add-customer-lane-commitments-board' ||
      isLoading ||
      !selectedCustomerLaneCommitmentsBoard
    ) {
      return;
    }
    setIsLoading(true);
    await markBoardAsFilteredByEntity(selectedCustomerLaneCommitmentsBoard);

    await addTabToApp({
      type: 'board',
      typeId: selectedCustomerLaneCommitmentsBoard.id,
    });
    setIsLoading(false);
  }, [
    addTabToApp,
    addTabType,
    isLoading,
    markBoardAsFilteredByEntity,
    selectedCustomerLaneCommitmentsBoard,
  ]);

  const onCopyScorecardSave = useCallback(async () => {
    if (addTabType !== 'copy-scorecard' || isLoading || !selectedScorecard) {
      return;
    }
    setIsLoading(true);

    const { newScorecardId } = await createScorecardCopy({
      scorecard: {
        ...selectedScorecard,
        labelIds,
      },
      title,
    });
    await addTabToApp({
      type: 'scorecard',
      typeId: newScorecardId,
    });
    setIsLoading(false);
  }, [
    addTabToApp,
    addTabType,
    createScorecardCopy,
    isLoading,
    labelIds,
    selectedScorecard,
    title,
  ]);

  const onSave = useCallback(async () => {
    if (addTabType === 'add-dashboard') {
      await onAddDashboardSave();
    } else if (addTabType === 'copy-dashboard') {
      await onCopyDashboardSave();
    } else if (addTabType === 'new-dashboard') {
      await onNewDashboardSave();
    } else if (addTabType === 'add-performance-board') {
      await onAddPerformanceBoardSave();
    } else if (addTabType === 'add-report') {
      await onAddReportSave();
    } else if (addTabType === 'add-driver-bonus-program-report') {
      await onAddDriverBonusProgramReportSaved();
    } else if (addTabType === 'add-driver-bonus-combined') {
      await onAddDriverBonusCombinedSaved();
    } else if (addTabType === 'add-driver-details') {
      await onAddDriverDetailsSaved();
    } else if (addTabType === 'add-customer-lane-commitments-board') {
      await onAddCustomerLaneCommitmentsBoardSave();
    } else if (addTabType === 'copy-scorecard') {
      await onCopyScorecardSave();
    }
  }, [
    addTabType,
    onAddDashboardSave,
    onCopyDashboardSave,
    onNewDashboardSave,
    onAddPerformanceBoardSave,
    onAddReportSave,
    onAddDriverBonusProgramReportSaved,
    onAddDriverBonusCombinedSaved,
    onAddDriverDetailsSaved,
    onAddCustomerLaneCommitmentsBoardSave,
    onCopyScorecardSave,
  ]);

  const getIsValid = useCallback(() => {
    if (addTabType === 'add-dashboard') {
      return !!selectedDashboard;
    } else if (addTabType === 'copy-dashboard') {
      return !!selectedDashboard;
    } else if (addTabType === 'new-dashboard') {
      return title !== '';
    } else if (addTabType === 'add-performance-board') {
      return !!selectedPerformanceBoard;
    } else if (addTabType === 'add-report') {
      return !!selectedReport;
    } else if (addTabType === 'add-driver-bonus-program-report') {
      return !!selectedBonusProgram;
    } else if (addTabType === 'add-driver-details') {
      return true;
    } else if (addTabType === 'add-driver-bonus-combined') {
      return true;
    } else if (addTabType === 'add-customer-lane-commitments-board') {
      return !!selectedCustomerLaneCommitmentsBoard;
    } else if (addTabType === 'copy-scorecard') {
      return !!selectedScorecard;
    } else {
      return false;
    }
  }, [
    addTabType,
    selectedDashboard,
    title,
    selectedPerformanceBoard,
    selectedReport,
    selectedBonusProgram,
    selectedCustomerLaneCommitmentsBoard,
    selectedScorecard,
  ]);

  const getSaveText = useCallback(() => {
    if (addTabType === 'add-dashboard') {
      return 'Add Dashboard';
    } else if (addTabType === 'copy-dashboard') {
      return 'Copy Dashboard';
    } else if (addTabType === 'new-dashboard') {
      return 'Create Dashboard';
    } else if (addTabType === 'add-performance-board') {
      return 'Add Performance Board';
    } else if (addTabType === 'add-report') {
      return 'Add Report';
    } else if (addTabType === 'add-driver-bonus-program-report') {
      return 'Add Driver Bonus Program Report';
    } else if (addTabType === 'add-driver-details') {
      return 'Add Driver Details';
    } else if (addTabType === 'add-driver-bonus-combined') {
      return 'Add Combined Bonus Tab';
    } else if (addTabType === 'add-customer-lane-commitments-board') {
      return 'Add Customer Lane Commitments Board';
    } else if (addTabType === 'copy-scorecard') {
      return 'Add KPI List';
    }
  }, [addTabType]);

  const isShowingLabelsPicker = useMemo(() => {
    return (
      (addTabType === 'copy-scorecard' && !!selectedScorecard) ||
      (addTabType === 'copy-dashboard' && !!selectedDashboard) ||
      addTabType === 'new-dashboard'
    );
  }, [addTabType, selectedDashboard, selectedScorecard]);

  const isValid = getIsValid();
  const saveText = getSaveText();

  return (
    <AllDefinitionsGate>
      <Form>
        <FormHeader title={formTitle} onClose={close} />
        <FormContent>
          <div style={{ marginBottom: 16 }}>
            <Inputs.Radio options={options} />
          </div>

          {addTabType === 'add-dashboard' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Dashboard to add
              </Typography.Body>
              <Inputs.Dropdown
                options={dashboardOptions}
                isSearchEnabled
                placeholder="Select a dashboard"
                testId={'select-dashboard'}
              />
            </div>
          )}
          {addTabType === 'copy-dashboard' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Dashboard to copy
              </Typography.Body>
              <Inputs.Dropdown
                options={dashboardOptions}
                isSearchEnabled
                placeholder="Select a dashboard"
                testId={'select-dashboard'}
              />
            </div>
          )}
          {addTabType === 'add-performance-board' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Performance Board to add
              </Typography.Body>
              <Inputs.Dropdown
                options={performanceBoardOptions}
                isSearchEnabled
                placeholder="Select a Performance Board"
                testId={'select-performance-board'}
              />
            </div>
          )}
          {addTabType === 'add-report' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Report to add
              </Typography.Body>
              <Inputs.Dropdown
                options={reportOptions}
                isSearchEnabled
                placeholder="Select a Report"
                testId={'select-report'}
              />
            </div>
          )}
          {addTabType === 'add-driver-bonus-program-report' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Bonus Program
              </Typography.Body>
              <Inputs.Dropdown
                options={bonusProgramOptions}
                isSearchEnabled
                placeholder="Select a Bonus Program"
                testId={'select-bonus-program'}
              />
            </div>
          )}
          {addTabType === 'copy-scorecard' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a KPI List to copy
              </Typography.Body>
              <Inputs.Dropdown
                options={scorecardOptions}
                isSearchEnabled
                placeholder="Select a KPI List"
                testId={'select-kpi-list'}
              />
            </div>
          )}

          {(addTabType === 'new-dashboard' ||
            (addTabType === 'copy-dashboard' && selectedDashboard)) && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                New Dashboard Name (Max 40 Characters)
              </Typography.Body>
              <Inputs.TextInput
                value={title}
                onChange={onTitleChanged}
                maxLength={40}
                placeholder="Name your dashboard"
                data-testid="new-dash-name"
              />
            </div>
          )}

          {addTabType === 'copy-scorecard' && selectedScorecard && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                New KPI List Name (Max 40 Characters)
              </Typography.Body>
              <Inputs.TextInput
                value={title}
                onChange={onTitleChanged}
                maxLength={40}
                placeholder="Name your KPI List"
                data-testid="new-kpi-list-name"
              />
            </div>
          )}

          {isShowingLabelsPicker && (
            <>
              <Typography.Body type="Label">Labels</Typography.Body>
              <LabelsPicker labelIds={labelIds} setLabelIds={setLabelIds} />
            </>
          )}

          {addTabType === 'add-customer-lane-commitments-board' && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Body type="Label">
                Select a Customer Lane Commitments Board to add
              </Typography.Body>
              <Inputs.Dropdown
                options={customerLaneCommitmentBoardOptions}
                isSearchEnabled
                placeholder="Select a Board"
                testId={'select-customer-lane-commitments-board'}
              />
            </div>
          )}
        </FormContent>
        <Row spaceBetween centerAlign>
          <Button onClick={close} label="Cancel" type="Tertiary" size="Small" />
          <Button
            onClick={onSave}
            isDisabled={!isValid || isLoading}
            label={saveText}
            isLoading={isLoading}
            type="Primary"
            size="Small"
          />
        </Row>
      </Form>
    </AllDefinitionsGate>
  );
};

export default AddTabWizard;
